import axios, { AxiosInstance } from 'axios'
import Env from '@/env'
import { HttpClient } from './http-client.interface'
import { HttpParams } from './http-params.interface'
import { HttpResponse } from '@/app/common/services/http/http-response'
import { BlobResponse } from '@/app/common/services/http/blob-response.entity'
import { HttpRequestBody } from '@/app/common/services/http/http-request-body'
import ErrorTranslator from '@/app/common/services/error-translator.service'
import i18n from '@/plugins/i18n'
import { TWOFACTOR_RATE_LIMITS } from '@/app/auth/services/twofactor-storage.service'

/**
 * Custom error for 2FA rate limiting that includes retry info
 */
export class TwoFactorRateLimitError extends Error {
    public readonly errorCode: string
    public readonly retryAfterMinutes: number
    public readonly retryAfterSeconds: number

    constructor(message: string, errorCode: string, retryAfterMinutes: number, retryAfterSeconds: number) {
        super(message)
        this.name = 'TwoFactorRateLimitError'
        this.errorCode = errorCode
        this.retryAfterMinutes = retryAfterMinutes
        this.retryAfterSeconds = retryAfterSeconds
    }
}

export class AxiosClient implements HttpClient {
    protected static instance: AxiosInstance

    constructor() {
        if (!AxiosClient.instance) {
            AxiosClient.instance = axios.create({
                baseURL: Env.apiUrl,
            })
        }
    }

    /**
     * This should be an internal implementation and not exposed
     * publically or via the interface. It was done for interceptors,
     * so it should be reverted to "protected" once a better solution is found.
     */
    public getClient(): AxiosInstance {
        return AxiosClient.instance
    }

    /**
     * Extract single language from bilingual message (format: "Dutch / English")
     */
    private extractLocalizedMessage(bilingualMessage: string): string {
        if (!bilingualMessage || !bilingualMessage.includes(' / ')) {
            return bilingualMessage
        }
        const parts = bilingualMessage.split(' / ')
        const locale = i18n.locale
        // Dutch (nl) is the first part, English (en) is the second
        return locale === 'en' ? (parts[1] || parts[0]) : parts[0]
    }

    /**
     * Handle 2FA errors - throws TwoFactorRateLimitError for rate limits, returns message for others
     */
    private handleTwoFactorError(errorData: any): void {
        const errorCode = errorData?.error
        if (!errorCode) return

        // Check if this is a rate limit error - throw special error with retry info
        if (errorCode === 'VERIFY_RATE_LIMITED' || errorCode === 'EMAIL_RATE_LIMITED') {
            // Use appropriate defaults based on error type:
            // - VERIFY_RATE_LIMITED: OTP verification lockout (15 minutes)
            // - EMAIL_RATE_LIMITED: Email send rate limit (10 minutes)
            const defaultSeconds = errorCode === 'VERIFY_RATE_LIMITED'
                ? TWOFACTOR_RATE_LIMITS.VERIFY_LOCKOUT_SECONDS
                : TWOFACTOR_RATE_LIMITS.EMAIL_SEND_LOCKOUT_SECONDS
            const defaultMinutes = Math.ceil(defaultSeconds / 60)

            // Backend uses snake_case, so check both formats
            const retryAfterMinutes = errorData.retry_after_minutes || errorData.retryAfterMinutes || defaultMinutes
            const retryAfterSeconds = errorData.retry_after_seconds || errorData.retryAfterSeconds || defaultSeconds

            // Use API message if available, otherwise generate localized fallback
            let message: string
            if (errorData.message) {
                message = this.extractLocalizedMessage(errorData.message)
            } else {
                // Generate localized fallback message
                message =
                    i18n.locale === 'en'
                        ? `Too many attempts. Please try again in ${retryAfterMinutes} minutes.`
                        : `Te veel pogingen. Probeer opnieuw over ${retryAfterMinutes} minuten.`
            }

            throw new TwoFactorRateLimitError(message, errorCode, retryAfterMinutes, retryAfterSeconds)
        }

        // For other 2FA errors, throw a regular error with localized message
        const twoFactorErrors = ['INVALID_OTP', 'NO_EMAIL']
        if (twoFactorErrors.includes(errorCode) && errorData.message) {
            throw new Error(this.extractLocalizedMessage(errorData.message))
        }
    }

    public async get(url: string, params?: HttpParams): Promise<HttpResponse> {
        try {
            const { data } = await this.getClient().get(url, params)

            return new HttpResponse(data)
        } catch (err) {
            // Prefer API's message if available, otherwise translate the error code
            const message = err.response?.data?.message
            if (message) {
                throw new Error(message)
            }
            throw new Error(new ErrorTranslator(err.response?.data?.error).translate())
        }
    }

    public async put(url: string, body?: HttpParams): Promise<HttpResponse> {
        try {
            const { data } = await this.getClient().put(url, new HttpRequestBody(body).transform())

            return new HttpResponse(data)
        } catch (err) {
            // Handle 2FA errors specially (may throw TwoFactorRateLimitError)
            this.handleTwoFactorError(err.response?.data)
            // Fall back to API's message if available
            const message = err.response?.data?.message
            if (message) {
                throw new Error(message)
            }
            const code = err.response?.data?.error?.code ?? err.response?.data?.error
            throw new Error(new ErrorTranslator(code).translate())
        }
    }

    public async post(url: string, body?: HttpParams, config?: HttpParams): Promise<HttpResponse> {
        try {
            const { data } = await this.getClient().post(url, new HttpRequestBody(body).transform(), config)

            return new HttpResponse(data)
        } catch (err) {
            // Handle 2FA errors specially (may throw TwoFactorRateLimitError)
            this.handleTwoFactorError(err.response?.data)
            // Fall back to API's message if available
            const message = err.response?.data?.message
            if (message) {
                throw new Error(message)
            }
            const code = err.response?.data?.error?.code ?? err.response?.data?.error
            throw new Error(new ErrorTranslator(code).translate())
        }
    }

    public async multiPart(url: string, body?: FormData): Promise<HttpResponse> {
        try {
            const { data } = await this.getClient().post(url, body, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })

            return new HttpResponse(data)
        } catch (err) {
            // Prefer API's message if available, otherwise translate the error code
            const message = err.response?.data?.message
            if (message) {
                throw new Error(message)
            }
            const code = err.response?.data?.error?.code ?? err.response?.data?.error
            throw new Error(new ErrorTranslator(code).translate())
        }
    }

    public async download(url: string, params?: HttpParams): Promise<BlobResponse> {
        try {
            const response = await this.getClient().get(url, params)

            return {
                data: response.data,
                headers: response.headers
            }
        } catch (err) {
            // Prefer API's message if available, otherwise translate the error code
            const message = err.response?.data?.message
            if (message) {
                throw new Error(message)
            }
            const code = err.response?.data?.error?.code ?? err.response?.data?.error
            throw new Error(new ErrorTranslator(code).translate())
        }
    }
}
